import React, { useState } from "react";
import SidebarOption from "./SidebarOption";
import { Plus, Inbox, Users, UserPlus, Settings, HelpCircle, CreditCard, BarChart3, FileText, Phone, PhoneIncoming } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { NEW_REQUEST_PAGE, ACCOUNT, REQUEST, BILLING, INSIGHTS, TEAM_MEMBERS, CALL_SCRIPTS, VERIFICATIONS, PATIENT_CALLS } from "utils/reducers";
import HelpDialog from "./HelpDialog";

interface SidebarProps {
  inboxCount: number;
  isTeamInbox: boolean;
  setIsTeamInbox: (isTeamInbox: boolean) => void;
  isSearching: boolean;
  setIsSearching: (isSearching: boolean) => void;
  showInboundCalls: boolean;
}

// Simple button component for utility options
function SidebarUtilityButton({
  Icon,
  name,
  onClick,
}: {
  Icon: React.ComponentType<any>;
  name: string;
  onClick: () => void;
}) {
  return (
    <button
      className="flex items-center gap-3 px-4 py-2.5 mx-1 my-0.5 rounded-lg cursor-pointer transition-all duration-200 text-gray-700 hover:bg-gray-100 hover:text-gray-900 font-normal w-full text-left"
      onClick={onClick}
    >
      <Icon className="w-5 h-5" />
      <span className="text-sm">{name}</span>
    </button>
  );
}

function Sidebar({
  inboxCount,
  isTeamInbox,
  setIsTeamInbox,
  isSearching,
  setIsSearching,
  showInboundCalls,
}: SidebarProps) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isInboundCallsActive = location.pathname.startsWith(
    "/dashboard/inbound_calls"
  );
  const isClearCacheRoute = location.pathname === "/dashboard/clear-cache";
  const showBilling = useSelector((state: any) => state.showBilling.show);
  const showInsights = useSelector((state: any) => state.showInsights.show);
  const showTeamMembers = useSelector((state: any) => state.showTeamMembers.show);
  const showCallScripts = useSelector((state: any) => state.showCallScripts.show);
  const showVerifications = useSelector((state: any) => state.showVerifications.show);
  const showPatientCalls = useSelector((state: any) => state.showPatientCalls.show);
  const [showHelpDialog, setShowHelpDialog] = useState(false);

  const hideOtherPages = () => {
    dispatch({ type: ACCOUNT.hide });
    dispatch({ type: BILLING.hide });
    dispatch({ type: INSIGHTS.hide });
    dispatch({ type: TEAM_MEMBERS.hide });
    dispatch({ type: CALL_SCRIPTS.hide });
    dispatch({ type: VERIFICATIONS.hide });
    dispatch({ type: PATIENT_CALLS.hide });
    dispatch({ type: REQUEST.hide });
    dispatch({ type: NEW_REQUEST_PAGE.hide });
  };

  const goToRoutedPage = (path: string, displayAction: { type: string }) => {
    dispatch({ type: ACCOUNT.hide });
    dispatch({ type: REQUEST.hide });
    dispatch({ type: NEW_REQUEST_PAGE.hide });
    dispatch({ type: BILLING.hide });
    dispatch({ type: INSIGHTS.hide });
    dispatch({ type: TEAM_MEMBERS.hide });
    dispatch({ type: CALL_SCRIPTS.hide });
    dispatch({ type: VERIFICATIONS.hide });
    dispatch({ type: PATIENT_CALLS.hide });
    dispatch(displayAction);
    setIsSearching(false);
    setIsTeamInbox(false);
    navigate(path);
  };

  const noRoutedPageActive =
    !showBilling &&
    !showInsights &&
    !showTeamMembers &&
    !showCallScripts &&
    !showVerifications &&
    !showPatientCalls &&
    !isClearCacheRoute;

  return (
    <div className="flex flex-col h-full ">
      {/* Request Button */}
      <div className="p-6 pb-4">
        <button
          className="w-full flex items-center justify-center gap-2.5 px-6 py-3 bg-brand hover:bg-brand-hover text-white rounded-full font-medium text-sm transition-all duration-200 shadow-sm hover:shadow-md"
          onClick={() => {
            hideOtherPages();
            dispatch({ type: NEW_REQUEST_PAGE.display });
            if (isClearCacheRoute) {
              navigate("/dashboard");
            }
          }}
        >
          <Plus className="w-4 h-4" />
          <span>New Request</span>
        </button>
      </div>

      {/* Navigation Options */}
      <nav className="flex-1 px-4 pt-2">
        <SidebarOption
          Icon={Inbox}
          name="My Inbox"
          number={inboxCount}
          active={
            !isSearching &&
            !isTeamInbox &&
            noRoutedPageActive &&
            !isInboundCallsActive
          }
          onClick={() => {
            hideOtherPages();
            setIsSearching(false);
            setIsTeamInbox(false);
            navigate("/dashboard");
          }}
        />
        <SidebarOption
          Icon={Users}
          name="Team Inbox"
          number={inboxCount}
          active={
            !isSearching &&
            isTeamInbox &&
            noRoutedPageActive &&
            !isInboundCallsActive
          }
          onClick={() => {
            hideOtherPages();
            setIsSearching(false);
            setIsTeamInbox(true);
            navigate("/dashboard");
          }}
        />

        {!showInboundCalls && (
          <SidebarOption
            Icon={Phone}
            name="Patient Calls"
            number={0}
            active={showPatientCalls}
            onClick={() => {
              goToRoutedPage("/dashboard/patient-calls", { type: PATIENT_CALLS.display });
            }}
          />
        )}

        {showInboundCalls && (
          <SidebarOption
            Icon={PhoneIncoming}
            name="Inbound Calls"
            number={0}
            active={isInboundCallsActive}
            onClick={() => {
              hideOtherPages();
              setIsSearching(false);
              setIsTeamInbox(false);
              navigate("/dashboard/inbound_calls");
            }}
          />
        )}

        <div className="my-3 mx-4 border-t border-gray-200" />

        <SidebarOption
          Icon={FileText}
          name="Call Scripts"
          number={0}
          active={showCallScripts}
          onClick={() => {
            goToRoutedPage("/dashboard/call-scripts", { type: CALL_SCRIPTS.display });
          }}
        />

        <SidebarOption
          Icon={HelpCircle}
          name="Verifications"
          number={0}
          active={showVerifications}
          onClick={() => {
            goToRoutedPage("/dashboard/verifications", { type: VERIFICATIONS.display });
          }}
        />

        <SidebarOption
          Icon={BarChart3}
          name="Insights"
          number={0}
          active={showInsights}
          onClick={() => {
            goToRoutedPage("/dashboard/insights", { type: INSIGHTS.display });
          }}
        />

        <SidebarOption
          Icon={UserPlus}
          name="Team Members"
          number={0}
          active={showTeamMembers}
          onClick={() => {
            goToRoutedPage("/dashboard/team-members", { type: TEAM_MEMBERS.display });
          }}
        />

        <SidebarOption
          Icon={CreditCard}
          name="Billing"
          number={0}
          active={showBilling}
          onClick={() => {
            goToRoutedPage("/dashboard/billing", { type: BILLING.display });
          }}
        />
      </nav>

      {/* Utility Options - Mobile Only */}
      <div className="md:hidden border-t border-gray-200 px-4 py-3 mt-auto">
        <SidebarUtilityButton
          Icon={Settings}
          name="Account"
          onClick={() => {
            hideOtherPages();
            dispatch({ type: ACCOUNT.display });
            navigate("/dashboard");
          }}
        />
        <SidebarUtilityButton
          Icon={HelpCircle}
          name="Help"
          onClick={() => setShowHelpDialog(true)}
        />
      </div>

      {/* Help Dialog */}
      <HelpDialog open={showHelpDialog} onOpenChange={setShowHelpDialog} />
    </div>
  );
}

export default Sidebar;
